import { configureStore, isPlain } from '@reduxjs/toolkit'
import { authApi } from './api/auth-api'
import { stampsApi } from './api/stamps-api'
import { stampOrdersApi } from './api/stamp-orders-api'
import { rollersApi } from './api/rollers-api'
import { materialGroupsApi } from './api/material-groups-api'
import { usersApi } from './api/users-api'
import { contractorsApi } from './api/contractors-api'
import authSlice from './auth/auth'
import editingPositionsSlice from './calculations/editing-positions-slice'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import { stampWriteOffsApi } from './api/stamp-write-offs-api'
import { receiptsApi } from './api/receipts-api'
import { receiptPositionsApi } from './api/receipt-positions-api'
import { tubesApi } from './api/tubes-api'
import { materialTypesApi } from './api/material-types-api'
import { materialsApi } from './api/materials-api'
import { stocksApi } from './api/stocks-api'
import { calculationsApi } from './api/calculations-api'
import { calculationPositionsApi } from './api/calculation-positions-api'
import { ribbonsApi } from './api/ribbons-api'
import { ordersApi } from './api/orders-api'
import { orderItemsApi } from './api/order-items-api'
import { productTypesApi } from './api/product-types-api'
import { shipmentPositionsApi } from './api/shipment-positions-api'
import { shipmentsApi } from './api/shipments-api'
import { currencyRateApi } from './api/currency-rate'
import { orderRequestsApi } from './api/order-requests-api'
import { machinesApi } from './api/machines-api'
import { lacquerTypesApi } from './api/lacquer-types-api'
import { tasksApi } from './api/tasks-api'
import { uploadApi } from './api/upload-api'
import { machineBrandsApi } from './api/machine-brands-api'
import { ordersReproparkApi } from './repropark-api/orders-api'
import { contractorsTypesApi } from './api/contractor-types-api'
import { printJobsApi } from './api/print-jobs-api'
import { printQueueEntriesApi } from './api/print-queue-entries-api'
import { rewindJobsApi } from './api/rewind-jobs-api'
import { rewindQueueEntriesApi } from './api/rewind-queue-entries-api'
import { printJobExecutorsApi } from './api/print-job-executors-api'
import { rewindJobExecutorsApi } from './api/rewind-job-executors-api'
import { formsApi } from './api/forms-api'
import { formSetsApi } from './api/form-sets-api'
import { formOrdersApi } from './api/form-orders-api'
import { contractorContactsApi } from './api/contractor-contacts-api'
import { additionalJobsApi } from './api/additional-jobs-api'
import { teamsApi } from './api/teams-api'
import { shiftTypesApi } from './api/shift-types-api'
import { shiftSchedulesApi } from './api/shift-schedules-api'
import { dadataApi } from './api/dadata-api'
import { overtimesApi } from './api/overtimes-api'
import { messageBarApi } from './api/message-bar-api'

export const store = configureStore({
    devTools: true,
    reducer: {
        auth: authSlice,
        editingPositions: editingPositionsSlice,
        [authApi.reducerPath]: authApi.reducer,
        [stampsApi.reducerPath]: stampsApi.reducer,
        [stampOrdersApi.reducerPath]: stampOrdersApi.reducer,
        [rollersApi.reducerPath]: rollersApi.reducer,
        [materialGroupsApi.reducerPath]: materialGroupsApi.reducer,
        [usersApi.reducerPath]: usersApi.reducer,
        [contractorsApi.reducerPath]: contractorsApi.reducer,
        [stampWriteOffsApi.reducerPath]: stampWriteOffsApi.reducer,
        [receiptsApi.reducerPath]: receiptsApi.reducer,
        [receiptPositionsApi.reducerPath]: receiptPositionsApi.reducer,
        [tubesApi.reducerPath]: tubesApi.reducer,
        [materialTypesApi.reducerPath]: materialTypesApi.reducer,
        [materialsApi.reducerPath]: materialsApi.reducer,
        [stocksApi.reducerPath]: stocksApi.reducer,
        [calculationsApi.reducerPath]: calculationsApi.reducer,
        [calculationPositionsApi.reducerPath]: calculationPositionsApi.reducer,
        [ribbonsApi.reducerPath]: ribbonsApi.reducer,
        [ordersApi.reducerPath]: ordersApi.reducer,
        [orderItemsApi.reducerPath]: orderItemsApi.reducer,
        [productTypesApi.reducerPath]: productTypesApi.reducer,
        [shipmentPositionsApi.reducerPath]: shipmentPositionsApi.reducer,
        [shipmentsApi.reducerPath]: shipmentsApi.reducer,
        [currencyRateApi.reducerPath]: currencyRateApi.reducer,
        [orderRequestsApi.reducerPath]: orderRequestsApi.reducer,
        [machinesApi.reducerPath]: machinesApi.reducer,
        [lacquerTypesApi.reducerPath]: lacquerTypesApi.reducer,
        [tasksApi.reducerPath]: tasksApi.reducer,
        [uploadApi.reducerPath]: uploadApi.reducer,
        [machineBrandsApi.reducerPath]: machineBrandsApi.reducer,
        [ordersReproparkApi.reducerPath]: ordersReproparkApi.reducer,
        [contractorsTypesApi.reducerPath]: contractorsTypesApi.reducer,
        [printJobsApi.reducerPath]: printJobsApi.reducer,
        [printQueueEntriesApi.reducerPath]: printQueueEntriesApi.reducer,
        [rewindJobsApi.reducerPath]: rewindJobsApi.reducer,
        [rewindQueueEntriesApi.reducerPath]: rewindQueueEntriesApi.reducer,
        [printJobExecutorsApi.reducerPath]: printJobExecutorsApi.reducer,
        [rewindJobExecutorsApi.reducerPath]: rewindJobExecutorsApi.reducer,
        [formsApi.reducerPath]: formsApi.reducer,
        [formSetsApi.reducerPath]: formSetsApi.reducer,
        [formOrdersApi.reducerPath]: formOrdersApi.reducer,
        [contractorContactsApi.reducerPath]: contractorContactsApi.reducer,
        [additionalJobsApi.reducerPath]: additionalJobsApi.reducer,
        [teamsApi.reducerPath]: teamsApi.reducer,
        [shiftTypesApi.reducerPath]: shiftTypesApi.reducer,
        [shiftSchedulesApi.reducerPath]: shiftSchedulesApi.reducer,
        [dadataApi.reducerPath]: dadataApi.reducer,
        [overtimesApi.reducerPath]: overtimesApi.reducer,
        [messageBarApi.reducerPath]: messageBarApi.reducer,
    },
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [
                    'shipmentsApi/executeMutation/fulfilled', // либо регуляркой для универсальности
                ],
                isSerializable: (value: any) =>
                    isPlain(value) || value instanceof Blob || value instanceof File,
            },
        })
            .concat(authApi.middleware)
            .concat(stampsApi.middleware)
            .concat(stampOrdersApi.middleware)
            .concat(rollersApi.middleware)
            .concat(materialGroupsApi.middleware)
            .concat(usersApi.middleware)
            .concat(contractorsApi.middleware)
            .concat(receiptsApi.middleware)
            .concat(receiptPositionsApi.middleware)
            .concat(stampWriteOffsApi.middleware)
            .concat(stampWriteOffsApi.middleware)
            .concat(materialTypesApi.middleware)
            .concat(materialsApi.middleware)
            .concat(stocksApi.middleware)
            .concat(tubesApi.middleware)
            .concat(orderRequestsApi.middleware)
            .concat(calculationPositionsApi.middleware)
            .concat(calculationsApi.middleware)
            .concat(ribbonsApi.middleware)
            .concat(ordersApi.middleware)
            .concat(orderItemsApi.middleware)
            .concat(productTypesApi.middleware)
            .concat(shipmentPositionsApi.middleware)
            .concat(shipmentsApi.middleware)
            .concat(currencyRateApi.middleware)
            .concat(lacquerTypesApi.middleware)
            .concat(machinesApi.middleware)
            .concat(uploadApi.middleware)
            .concat(tasksApi.middleware)
            .concat(machineBrandsApi.middleware)
            .concat(machinesApi.middleware)
            .concat(ordersReproparkApi.middleware)
            .concat(contractorsTypesApi.middleware)
            .concat(printJobsApi.middleware)
            .concat(printQueueEntriesApi.middleware)
            .concat(rewindJobsApi.middleware)
            .concat(rewindQueueEntriesApi.middleware)
            .concat(printJobExecutorsApi.middleware)
            .concat(rewindJobExecutorsApi.middleware)
            .concat(ordersReproparkApi.middleware)
            .concat(dadataApi.middleware)
            .concat(formsApi.middleware)
            .concat(formSetsApi.middleware)
            .concat(formOrdersApi.middleware)
            .concat(contractorContactsApi.middleware)
            .concat(additionalJobsApi.middleware)
            .concat(teamsApi.middleware)
            .concat(shiftTypesApi.middleware)
            .concat(shiftSchedulesApi.middleware)
            .concat(overtimesApi.middleware)
            .concat(messageBarApi.middleware),
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatchType = typeof store.dispatch
export const useAppDispatch = () => useDispatch<AppDispatchType>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

/**
 * Сбрасывает все кэши RTK Query API
 */
export const resetAllApiStates = () => {
    store.dispatch(authApi.util.resetApiState())
    store.dispatch(stampsApi.util.resetApiState())
    store.dispatch(stampOrdersApi.util.resetApiState())
    store.dispatch(rollersApi.util.resetApiState())
    store.dispatch(materialGroupsApi.util.resetApiState())
    store.dispatch(usersApi.util.resetApiState())
    store.dispatch(contractorsApi.util.resetApiState())
    store.dispatch(stampWriteOffsApi.util.resetApiState())
    store.dispatch(receiptsApi.util.resetApiState())
    store.dispatch(receiptPositionsApi.util.resetApiState())
    store.dispatch(tubesApi.util.resetApiState())
    store.dispatch(materialTypesApi.util.resetApiState())
    store.dispatch(materialsApi.util.resetApiState())
    store.dispatch(stocksApi.util.resetApiState())
    store.dispatch(calculationsApi.util.resetApiState())
    store.dispatch(calculationPositionsApi.util.resetApiState())
    store.dispatch(ribbonsApi.util.resetApiState())
    store.dispatch(ordersApi.util.resetApiState())
    store.dispatch(orderItemsApi.util.resetApiState())
    store.dispatch(productTypesApi.util.resetApiState())
    store.dispatch(shipmentPositionsApi.util.resetApiState())
    store.dispatch(shipmentsApi.util.resetApiState())
    store.dispatch(currencyRateApi.util.resetApiState())
    store.dispatch(orderRequestsApi.util.resetApiState())
    store.dispatch(machinesApi.util.resetApiState())
    store.dispatch(lacquerTypesApi.util.resetApiState())
    store.dispatch(tasksApi.util.resetApiState())
    store.dispatch(uploadApi.util.resetApiState())
    store.dispatch(machineBrandsApi.util.resetApiState())
    store.dispatch(ordersReproparkApi.util.resetApiState())
    store.dispatch(contractorsTypesApi.util.resetApiState())
    store.dispatch(printJobsApi.util.resetApiState())
    store.dispatch(printQueueEntriesApi.util.resetApiState())
    store.dispatch(rewindJobsApi.util.resetApiState())
    store.dispatch(rewindQueueEntriesApi.util.resetApiState())
    store.dispatch(printJobExecutorsApi.util.resetApiState())
    store.dispatch(rewindJobExecutorsApi.util.resetApiState())
    store.dispatch(formsApi.util.resetApiState())
    store.dispatch(formSetsApi.util.resetApiState())
    store.dispatch(formOrdersApi.util.resetApiState())
    store.dispatch(additionalJobsApi.util.resetApiState())
    store.dispatch(teamsApi.util.resetApiState())
    store.dispatch(shiftTypesApi.util.resetApiState())
    store.dispatch(shiftSchedulesApi.util.resetApiState())
    store.dispatch(overtimesApi.util.resetApiState())
    store.dispatch(messageBarApi.util.resetApiState())
}
